import React, { useId } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

export interface ShopSlideItem {
	aplyStrtDtime?: string | number;
	aplyStopDtime?: string | number;
	bnrCtxt?: string;
	bnrDesc?: string;
	bnrFileUrl?: string;
	moBnrFileUrl?: string;
	bnrTitl?: string;
	gameSvcCd?: string;
	lnkTrgtCd?: string;
	lnkUrlAddr?: string;
	lnkUrlTxt?: string;
	id?: string | number;
}

interface ShopSlideUiProps {
	slides: ShopSlideItem[];
	className?: string;
	autoplayDelay?: number;
	onLayerPopup?: (slide: ShopSlideItem) => void;
	langData?: any;
	gameSvcCd?: string | null;
}

const ShopSlideUi = ({
	slides,
	className = "",
	autoplayDelay = 10000,
	onLayerPopup,
	langData,
}: ShopSlideUiProps) => {
	const navId = useId().replace(/:/g, "");
	const prevBtnClass = `shopSlideNavPrev-${navId}`;
	const nextBtnClass = `shopSlideNavNext-${navId}`;
	const validSlides = Array.isArray(slides)
		? slides.filter((slide) => !!slide?.bnrFileUrl || !!slide?.moBnrFileUrl)
		: [];

	if (validSlides.length === 0) {
		return null;
	}

	const canLoop = validSlides.length > 1;

	return (
		<div className={`shopSlideWrap ${className}`.trim()}>
			<Swiper
				modules={[Autoplay, Navigation, Pagination, A11y]}
				slidesPerView={1}
				loop={canLoop}
				navigation={
					canLoop
						? {
								prevEl: `.${prevBtnClass}`,
								nextEl: `.${nextBtnClass}`,
						  }
						: false
				}
				pagination={{ clickable: true }}
				autoplay={
					canLoop
						? {
							  delay: autoplayDelay,
							  disableOnInteraction: false,
							  pauseOnMouseEnter: true,
						  }
						: false
				}
				a11y={{ enabled: true }}
			>
				{validSlides.map((slide, index) => {
					const key = slide.id ?? `${slide.bnrFileUrl || slide.moBnrFileUrl || "shop-slide"}-${index}`;
					const alt = slide.bnrTitl || slide.bnrCtxt || `shop-slide-${index + 1}`;
					const desktopSrc = slide.bnrFileUrl || slide.moBnrFileUrl || "";
					const mobileSrc = slide.moBnrFileUrl || slide.bnrFileUrl || "";
					const linkType = String(slide.lnkTrgtCd || "LINK").toUpperCase();
					const isLink = linkType === "LINK";
					const isBlank = linkType === "BLNK" || linkType === "BLANK" || linkType === "_BLANK";
					const isWindowPopup = linkType === "WPOP";
					const isLayerPopup = linkType === "LPOP";
					const target = isBlank ? "_blank" : "_self";
					const href = slide.lnkUrlAddr;
					const rel = target === "_blank" ? "noreferrer noopener" : undefined;
					let linkTo: string | null = null;
					if (isLink && href) {
						if (href.startsWith("/")) {
							linkTo = href;
						} else if (typeof window !== "undefined") {
							try {
								const parsed = new URL(href, window.location.origin);
								if (parsed.origin === window.location.origin) {
									linkTo = `${parsed.pathname}${parsed.search}${parsed.hash}`;
								}
							} catch {
								linkTo = null;
							}
						}
					}
					const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
						if (!href) {
							return;
						}

						if (isWindowPopup) {
							e.preventDefault();
							window.open(href, "shopBannerPopup", "width=1200,height=800,resizable=yes,scrollbars=yes");
							return;
						}

						if (isLayerPopup) {
							e.preventDefault();
							onLayerPopup?.(slide);
						}
					};
					const image = (
						<div className="slideLinkWrap">
							<picture>
								<source media="(max-width: 1200px)" srcSet={mobileSrc} />
								<img src={desktopSrc} alt={alt} loading="lazy" />
							</picture>
							<img
								className="bnrFrame"
								src={process.env.PUBLIC_URL + "/images/Shop/bnrFrame.png"}
								alt=""
								aria-hidden="true"
							/>

						</div>
					);

					return (
						<SwiperSlide key={key}>
							{href && isLink && linkTo ? (
									<Link to={linkTo} onClick={onLinkClick}>
										{image}
									</Link>
							) : href ? (
									<a href={href} target={target} rel={rel} onClick={onLinkClick}>
										{image}
									</a>
							) : (
								image
							)}
						</SwiperSlide>
					);
				})}
			</Swiper>
			{canLoop ? (
				<>
					<button
						type="button"
						className={`shopSlideNavBtn shopSlideNavPrev swiper-button-prev ${prevBtnClass}`}
						aria-label={langData?.COMM_MAIN_PREV_BNR_BTN_00 || "이전 배너"}
					/>
					<button
						type="button"
						className={`shopSlideNavBtn shopSlideNavNext swiper-button-next ${nextBtnClass}`}
						aria-label={langData?.COMM_MAIN_NEXT_BNR_BTN_00 || "다음 배너"}
					/>
				</>
			) : null}
		</div>
	);
};

export default ShopSlideUi;
